import { Component } from '@theme/component';
import { mediaQueryLarge } from '@theme/utilities';

class ProductDetailsSticky extends Component {
  /** @type {number} */
  #lastScrollY = 0;

  /** @type {number} */
  #stickyTop = 0;

  /** @type {number} */
  #scrollDelta = 0;

  /** Desktop-only listeners: torn down and rebuilt on every breakpoint crossing. */
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** Lives for the whole connected lifetime: watches the breakpoint itself. */
  /** @type {AbortController | null} */
  #lifecycleController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#lifecycleController?.abort();
    this.#lifecycleController = new AbortController();

    // The offset is a desktop-only affordance, but the breakpoint can change without a reload.
    // Deciding once in connectedCallback left the column dead after any resize across 750px (and,
    // the other way round, kept a scroll listener running on a viewport that ignores its output).
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange, {
      signal: this.#lifecycleController.signal,
    });

    if (mediaQueryLarge.matches) this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#lifecycleController?.abort();
    this.#lifecycleController = null;

    this.#stop();
  }

  /**
   * Re-rendered by the Section Rendering API (color swap): `.product-details__inner` may be a fresh
   * node, so the ResizeObserver would keep watching the detached one — and the sibling's content has
   * its own height, hence a new scroll delta. The current offset is deliberately kept as-is: the
   * scroll position did not change, so resetting it would make the column jump.
   */
  updatedCallback() {
    super.updatedCallback();

    if (!mediaQueryLarge.matches) return;

    this.#observeContent();
    this.#calculateScrollDelta();
  }

  #handleBreakpointChange = () => {
    if (mediaQueryLarge.matches) this.#start();
    else this.#stop();
  };

  #start() {
    this.#stop();

    this.#lastScrollY = window.scrollY;
    this.#stickyTop = 0;
    this.#scrollDelta = 0;

    this.#calculateScrollDelta();

    this.#abortController = new AbortController();
    window.addEventListener('scroll', this.#handleScroll, {
      passive: true,
      signal: this.#abortController.signal,
    });

    this.#observeContent();
  }

  #stop() {
    this.#abortController?.abort();
    this.#abortController = null;

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    // A leftover negative offset would still be read by the desktop `top: calc(...)` for a frame
    // on the way back, before the first scroll event recomputes it.
    this.style.removeProperty('--sticky-top');
  }

  #observeContent() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(this.#calculateScrollDelta);
    this.#resizeObserver.observe(this.#getContentElement());
  }

  /** @returns {Element} */
  #getContentElement() {
    return this.querySelector('.product-details__inner') ?? this;
  }

  #calculateScrollDelta = () => {
    const contentHeight = this.#getContentElement().scrollHeight;
    const availableHeight = window.innerHeight;

    this.#scrollDelta = Math.max(0, contentHeight - availableHeight);
  };

  #handleScroll = () => {
    this.#calculateScrollDelta();

    if (this.#scrollDelta === 0) {
      this.#lastScrollY = window.scrollY;
      return;
    }

    const scrollTop = Math.max(window.scrollY, 0);
    const delta = scrollTop - this.#lastScrollY;

    this.#stickyTop = Math.max(-this.#scrollDelta, Math.min(0, this.#stickyTop - delta));
    this.style.setProperty('--sticky-top', `${this.#stickyTop}px`);
    this.#lastScrollY = scrollTop;
  };
}

if (!customElements.get('product-details-sticky')) {
  customElements.define('product-details-sticky', ProductDetailsSticky);
}
